<!-- app-filter-panel ──────────────────────────────────────────────────────────
  
Three rendering modes (auto-selected based on field config):

① STANDALONE  — type: 'text'|'select'|etc.   → plain input, no chrome
② RADIO GROUP — type: 'radio-group', n > 1   → radios → one active input
   AUTO-COLLAPSE when n = 1                  → plain input (no radios shown)
③ CHECKBOX GROUP — type: 'checkbox-group', n > 1  → checkboxes → checked inputs
   AUTO-COLLAPSE when n = 1                  → plain input (no checkboxes shown)

─────────────────────────────────────────────────────────────────────────────── -->
<div class="server-filter-container">

    <!-- ── Trigger button ──────────────────────────────────────────────────── -->
    <button type="button" class="filter-icon-btn" [class.active]="isOpen || isFilterApplied" [title]="buttonTitle"
        (click)="toggle()">
        <i class="bi bi-funnel-fill"></i>
        <span class="filter-badge" *ngIf="isFilterApplied"></span>
    </button>

    <!-- ── Dropdown ────────────────────────────────────────────────────────── -->
    <div class="server-filter-dropdown" *ngIf="isOpen">

        <div class="filter-dropdown-header">
            <h6>{{ title }}</h6>
            <button type="button" class="btn-close-filter" (click)="close()">
                <i class="bi bi-x"></i>
            </button>
        </div>

        <div class="filter-dropdown-body">
            <form [formGroup]="form" (ngSubmit)="apply()">

                <!-- ══════════════════════════════════════════════════════════════ -->
                <!-- Field loop                                                    -->
                <!-- ══════════════════════════════════════════════════════════════ -->
                <ng-container *ngFor="let field of fields; let last = last">

                    <!-- ────────────────────────────────────────────────────────── -->
                    <!-- MODE ① — STANDALONE input / select                        -->
                    <!-- ────────────────────────────────────────────────────────── -->
                    <ng-container *ngIf="isStandalone(field)">
                        <div class="filter-input-group" [class.mb-0]="last">
                            <label class="filter-label">{{ field.label }}</label>

                            <ng-container [ngTemplateOutlet]="fieldWidget" [ngTemplateOutletContext]="{
                                    type:        field.type,
                                    controlName: field.controlName,
                                    placeholder: field.placeholder,
                                    options:     field.options
                                }">
                            </ng-container>

                            <div class="invalid-feedback d-block" *ngIf="isFieldInvalid(field.controlName)">
                                {{ getError(field.controlName) }}
                            </div>
                            <small class="hint-text" *ngIf="field.hint">{{ field.hint }}</small>
                        </div>
                    </ng-container>

                    <!-- ────────────────────────────────────────────────────────── -->
                    <!-- MODE ② — RADIO GROUP                                      -->
                    <!-- ────────────────────────────────────────────────────────── -->
                    <ng-container *ngIf="isRadioGroup(field)">

                        <!-- ② AUTO-COLLAPSED (single option → plain input) --------- -->
                        <ng-container *ngIf="isCollapsed(field); else fullRadioGroup">
                            <div class="filter-input-group" [class.mb-0]="last">
                                <label class="filter-label">{{ collapsedLabel(field) }}</label>

                                <ng-container [ngTemplateOutlet]="fieldWidget" [ngTemplateOutletContext]="{
                                    type:        collapsedChild(field).type,
                                    controlName: collapsedChild(field).controlName,
                                    placeholder: collapsedChild(field).placeholder,
                                    options:     collapsedChild(field).options
                                    }">
                                </ng-container>

                                <div class="invalid-feedback d-block"
                                    *ngIf="isFieldInvalid(collapsedChild(field).controlName)">
                                    {{ getError(collapsedChild(field).controlName) }}
                                </div>
                                <small class="hint-text" *ngIf="collapsedChild(field).hint">
                                    {{ collapsedChild(field).hint }}
                                </small>
                            </div>
                        </ng-container>

                        <!-- ② FULL RADIO GROUP ------------------------------------- -->
                        <ng-template #fullRadioGroup>
                            <label class="filter-label">{{ field.label }}</label>

                            <div class="filter-options">
                                <div class="form-check" *ngFor="let opt of field.options">
                                    <input class="form-check-input" type="radio"
                                        [id]="field.controlName + '_' + opt.value" [value]="opt.value"
                                        [formControlName]="field.controlName">
                                    <label class="form-check-label" [for]="field.controlName + '_' + opt.value">
                                        {{ opt.label }}
                                    </label>
                                </div>
                            </div>

                            <!-- Active child input
                            IMPORTANT: rendered inline (not via ngTemplateOutlet) so that
                            Angular fully destroys + recreates the input element on each
                            radio switch. ngTemplateOutlet reuses the same DOM node and
                            just changes [formControlName], which causes the
                            ControlValueAccessor to lose sync with the new FormControl. -->
                            <ng-container *ngFor="let opt of field.options">
                                <ng-container *ngIf="isCheckedRadio(field.controlName, opt.value)">
                                    <div class="filter-input-group child-input-group" [class.mb-0]="last">
                                        <label class="filter-label">Enter Value:</label>

                                        <!-- SELECT -->
                                        <select *ngIf="opt.field.type === 'select'; else radioInputTpl"
                                            class="form-select filter-input" [formControlName]="opt.field.controlName"
                                            [class.is-invalid]="isFieldInvalid(opt.field.controlName)">
                                            <option value="">{{ opt.field.placeholder || 'Select an option' }}</option>
                                            <option *ngFor="let o of opt.field.options" [value]="o.value">{{ o.label }}
                                            </option>
                                        </select>

                                        <!-- INPUT -->
                                        <ng-template #radioInputTpl>
                                            <input [type]="opt.field.type" class="form-control filter-input"
                                                [placeholder]="opt.field.placeholder || ''"
                                                [formControlName]="opt.field.controlName"
                                                [class.is-invalid]="isFieldInvalid(opt.field.controlName)"
                                                (keyup.enter)="apply()">
                                        </ng-template>

                                        <div class="invalid-feedback d-block"
                                            *ngIf="isFieldInvalid(opt.field.controlName)">
                                            {{ getError(opt.field.controlName) }}
                                        </div>
                                        <small class="hint-text" *ngIf="opt.field.hint">
                                            {{ opt.field.hint }}
                                        </small>
                                    </div>
                                </ng-container>
                            </ng-container>
                        </ng-template>

                    </ng-container>

                    <!-- ────────────────────────────────────────────────────────── -->
                    <!-- MODE ③ — CHECKBOX GROUP                                   -->
                    <!-- ────────────────────────────────────────────────────────── -->
                    <ng-container *ngIf="isCheckboxGroup(field)">

                        <!-- ③ AUTO-COLLAPSED (single option → plain input) --------- -->
                        <ng-container *ngIf="isCollapsed(field); else fullCheckboxGroup">
                            <div class="filter-input-group" [class.mb-0]="last">
                                <label class="filter-label">{{ collapsedLabel(field) }}</label>

                                <ng-container [ngTemplateOutlet]="fieldWidget" [ngTemplateOutletContext]="{
                                    type:        collapsedChild(field).type,
                                    controlName: collapsedChild(field).controlName,
                                    placeholder: collapsedChild(field).placeholder,
                                    options:     collapsedChild(field).options
                                    }">
                                </ng-container>

                                <div class="invalid-feedback d-block"
                                    *ngIf="isFieldInvalid(collapsedChild(field).controlName)">
                                    {{ getError(collapsedChild(field).controlName) }}
                                </div>
                                <small class="hint-text" *ngIf="collapsedChild(field).hint">
                                    {{ collapsedChild(field).hint }}
                                </small>
                            </div>
                        </ng-container>

                        <!-- ③ FULL CHECKBOX GROUP ---------------------------------- -->
                        <ng-template #fullCheckboxGroup>
                            <label class="filter-label">{{ field.label }}</label>

                            <div class="filter-options checkbox-group-options">
                                <ng-container *ngFor="let opt of field.options">

                                    <!-- Checkbox row -->
                                    <div class="form-check checkbox-row">
                                        <input class="form-check-input" type="checkbox"
                                            [id]="field.controlName + '_' + opt.value"
                                            [formControlName]="chkControlName(field.controlName, opt.value)">
                                        <label class="form-check-label" [for]="field.controlName + '_' + opt.value">
                                            {{ opt.label }}
                                        </label>
                                    </div>

                                    <!-- Revealed child input (slide-in when checked) -->
                                    <div class="checkbox-child-input"
                                        [class.visible]="isChecked(field.controlName, opt.value)">
                                        <ng-container [ngTemplateOutlet]="fieldWidget" [ngTemplateOutletContext]="{
                                            type:        opt.field.type,
                                            controlName: opt.field.controlName,
                                            placeholder: opt.field.placeholder,
                                            options:     opt.field.options
                                            }">
                                        </ng-container>

                                        <div class="invalid-feedback d-block"
                                            *ngIf="isChecked(field.controlName, opt.value) && isFieldInvalid(opt.field.controlName)">
                                            {{ getError(opt.field.controlName) }}
                                        </div>
                                        <small class="hint-text" *ngIf="opt.field.hint">
                                            {{ opt.field.hint }}
                                        </small>
                                    </div>

                                </ng-container>
                            </div>
                        </ng-template>

                    </ng-container>

                    <!-- Spacer between fields -->
                    <div class="field-spacer" *ngIf="!last"></div>

                </ng-container>
                <!-- /Field loop -->

                <!-- ── Action buttons ──────────────────────────────────────────── -->
                <div class="filter-actions">
                    <button type="submit" class="btn btn-apply" [class.btn-apply--invalid]="form.invalid">
                        <i class="bi bi-check-circle me-1"></i>
                        Apply Filter
                    </button>
                    <button type="button" class="btn btn-clear" (click)="clear()">
                        <i class="bi bi-x-circle me-1"></i>
                        Clear
                    </button>
                </div>

                <!-- ══════════════════════════════════════════════════════════════ -->
                <!-- #fieldWidget MUST live inside <form [formGroup]="form">       -->
                <!-- formControlName resolves FormGroup through Angular's injector -->
                <!-- at template DEFINITION time — not at render/outlet time.      -->
                <!-- Placing it outside the form breaks the control binding.       -->
                <!-- ══════════════════════════════════════════════════════════════ -->
                <ng-template #fieldWidget let-type="type" let-controlName="controlName" let-placeholder="placeholder"
                    let-options="options">

                    <select *ngIf="type === 'select'; else inputWidget" class="form-select filter-input"
                        [formControlName]="controlName" [class.is-invalid]="isFieldInvalid(controlName)">
                        <option value="">{{ placeholder || 'Select an option' }}</option>
                        <option *ngFor="let opt of options" [value]="opt.value">{{ opt.label }}</option>
                    </select>

                    <ng-template #inputWidget>
                        <input [type]="type" class="form-control filter-input" [placeholder]="placeholder || ''"
                            [formControlName]="controlName" [class.is-invalid]="isFieldInvalid(controlName)"
                            (keyup.enter)="apply()">
                    </ng-template>

                </ng-template>

            </form>
        </div>

    </div>
</div>